import { getEffectivePosRank, parseRankNum } from './adpData';

// Rank-based tier system: maps posRank ranges to tier numbers and price ranges
// Each tier: { tier, min, max, floor, ceiling, label }
// min/max = posRank range (inclusive). floor/ceiling = fraction of startingBudget.
export const TIER_MAPS = {
  // RB re-cut (Aug 2026) — same rationale and fitting method as WR below. The
  // old T1 spanned ranks 1-2 and T2 ranks 3-4, so the board's RB4 ($46) drew his
  // bid baseline from the same band as RB3 ($49) and the old T2/T3 boundary fell
  // mid-decay.
  //
  // Bands are re-cut; LEVELS are held at the parent's rank-averaged tierValue
  // (see the commit message). Re-banding alone moves RB board#4/#1 from 0.92 to
  // the ~0.78 target, because rank 4 drops from T2 to T3. The MFL level targets
  // additionally wanted ranks 7-24 enriched by $5-11 each, which is a +$150/draft
  // RB inflation — that is what breaks position share and unspent, and it buys
  // nothing on the ratio the re-cut exists to fix.
  RB: [
    { tier: 1, min: 1,  max: 1,   floor: 0.274, ceiling: 0.358, label: "The Anchor" },        // draw $65.3
    { tier: 2, min: 2,  max: 3,   floor: 0.241, ceiling: 0.347, label: "Elite Pair" },        // draw $61.5
    { tier: 3, min: 4,  max: 6,   floor: 0.196, ceiling: 0.281, label: "High-End Starters" }, // draw $49.8
    { tier: 4, min: 7,  max: 11,  floor: 0.135, ceiling: 0.217, label: "Solid Starters" },    // draw $37.3
    { tier: 5, min: 12, max: 15,  floor: 0.086, ceiling: 0.173, label: "Value Starters" },    // draw $28.1
    { tier: 6, min: 16, max: 19,  floor: 0.073, ceiling: 0.122, label: "Late Starters" },     // draw $20.7
    { tier: 7, min: 20, max: 24,  floor: 0.049, ceiling: 0.088, label: "Fliers" },            // draw $14.7
    { tier: 8, min: 25, max: 40,  floor: 0.02,  ceiling: 0.04,  label: "Deep Bench" },
    { tier: 9, min: 41, max: 999, floor: 0.01,  ceiling: 0.02,  label: "Lottery Tickets" },
  ],
  // WR re-cut (Aug 2026) — bands and levels from 110 real 2025 MFL auctions,
  // ratio to board rank 1, scaled by the Smithtown 4-draft WR1 clearing price
  // ($56). The old T1 spanned ranks 1-3 at one level while the displayed board
  // decays 18% across them, which is what made board#3 the worst inversion cell
  // (see docs/reference/bid-decomp-aug5.txt).
  //
  // floor/ceiling are fitted on the MEAN DRAW, not the midpoint: tierValue is
  // floor + (0.25 + rand*0.75)*range, so E[draw] = 0.375*floor + 0.625*ceiling.
  // Targets are per-band mean tierValue backed out of the measured sale/tierValue
  // ratio per rank (npm run tier-ladder), because the pipeline lift is not
  // constant across bands.
  WR: [
    { tier: 1, min: 1,  max: 1,   floor: 0.216, ceiling: 0.312, label: "The Alpha" },        // draw $55.2
    { tier: 2, min: 2,  max: 3,   floor: 0.212, ceiling: 0.297, label: "The Alphas" },       // draw $53.0
    { tier: 3, min: 4,  max: 8,   floor: 0.179, ceiling: 0.242, label: "Strong Starters" },  // draw $43.7
    // Band WIDTH is a second, independent lever: it sets price dispersion without
    // moving the mean draw. Fitting the means with narrowed bands drove severe
    // inversions to 3.82 (below the real 4.10-5.00 range) and tau-b to 0.747
    // (above 0.72-0.74) — too orderly, which is a regression, not a win. These
    // keep the fitted means and restore the parent's ceiling/floor spread.
    { tier: 4, min: 9,  max: 11,  floor: 0.101, ceiling: 0.203, label: "Solid Starters" },   // draw $33.0
    { tier: 5, min: 12, max: 15,  floor: 0.068, ceiling: 0.170, label: "Mid-Tier" },         // draw $26.4
    { tier: 6, min: 16, max: 19,  floor: 0.051, ceiling: 0.128, label: "Value Plays" },      // draw $19.8
    { tier: 7, min: 20, max: 24,  floor: 0.031, ceiling: 0.123, label: "Fliers" },           // draw $17.7
    { tier: 8, min: 25, max: 36,  floor: 0.02,  ceiling: 0.04,  label: "Bench Stashes" },
    { tier: 9, min: 37, max: 999, floor: 0.01,  ceiling: 0.02,  label: "End of Bench" },
  ],
  QB: [
    { tier: 1, min: 1,  max: 1,   floor: 0.175, ceiling: 0.275, label: "The Premium" },
    { tier: 2, min: 2,  max: 2,   floor: 0.125, ceiling: 0.25,  label: "Strong Starter" },
    { tier: 3, min: 3,  max: 7,   floor: 0.10,  ceiling: 0.15,  label: "Mid-Tier" },
    { tier: 4, min: 8,  max: 11,  floor: 0.025, ceiling: 0.10,  label: "Cheap Starters" },
    { tier: 5, min: 12, max: 18,  floor: 0.015, ceiling: 0.03,  label: "Streamers" },
    { tier: 6, min: 19, max: 999, floor: 0.01,  ceiling: 0.015, label: "Deep Bench" },
  ],
  TE: [
    { tier: 1, min: 1,  max: 1,   floor: 0.15,  ceiling: 0.25,  label: "The Elite" },
    { tier: 2, min: 2,  max: 2,   floor: 0.135, ceiling: 0.185, label: "Premium" },
    { tier: 3, min: 3,  max: 3,   floor: 0.095, ceiling: 0.145, label: "Solid Starter" },
    { tier: 4, min: 4,  max: 5,   floor: 0.055, ceiling: 0.09,  label: "Value Starters" },
    { tier: 5, min: 6,  max: 7,   floor: 0.035, ceiling: 0.06,  label: "Streamers" },
    { tier: 6, min: 8,  max: 999, floor: 0.01,  ceiling: 0.02,  label: "Deep Bench" },
  ],
  DEF: [
    { tier: 1, min: 1, max: 3,   floor: 0.01,  ceiling: 0.025, label: "Elite DEF" },
    { tier: 2, min: 4, max: 8,   floor: 0.005, ceiling: 0.015, label: "Solid DEF" },
    { tier: 3, min: 9, max: 999, floor: 0.005, ceiling: 0.005, label: "Streamable" },
  ],
  K: [
    { tier: 1, min: 1, max: 2,   floor: 0.005, ceiling: 0.015, label: "Top K" },
    { tier: 2, min: 3, max: 5,   floor: 0.005, ceiling: 0.01,  label: "Solid K" },
    { tier: 3, min: 6, max: 999, floor: 0.005, ceiling: 0.005, label: "Streamable" },
  ],
};

// ── BAND RE-CUT (Aug 2026, chunk 3) — OFF BY DEFAULT ────────────────
//
// The maps above cut their bands on judgement; the displayed board cuts its own
// bands on the [frac, top, bottom] tuples in simConfig.js (mirrored in
// usePricing.js). The two disagree, and every band that STRADDLES a board cliff
// produces the sawtooth chunk 2 measured: tierValue is flat inside a band
// (auction.js:415 draws it uniformly) while the board decays inside it, so
// tv/board climbs across the band and resets at each boundary. WR T3 ran
// 1.010 -> 1.030 -> 1.390 -> 1.409 -> 1.436 across its five ranks. Both T3
// bands straddle a cliff: RB's board steps $46 -> $36 between ranks 4 and 5,
// WR's $44 -> $33 between 5 and 6.
//
// TIER_MAPS_RECUT puts the band edges exactly on the board's, at the 10-team
// 16-man reference config (derived and validated in bandRecut.scratch.js —
// repricing all 439 players from the re-derived tuples matches
// buildDynamicPriceMap exactly):
//
//   RB  1-2 | 3-4 | 5-8 | 9-12 | 13-17 | 18-20 | 21-23 | 24-44 | 45+
//   WR  1-2 | 3-5 | 6-9 | 10-14 | 15-19 | 20-23 | 24-27 | 28-53 | 54+
//
// Still NINE bands, the same count as the maps above — the count is load-
// bearing (auction.js:302 and :953 index this array positionally, :1029 takes
// its max tier, and generatePersonalTierSheet's SHIFT_CHANCES is keyed by tier
// number). Do not change it without auditing every reader.
//
// LEVELS ARE HELD, NOT RE-FITTED. Each band's mean draw is the rank-average of
// the mean draw the map above gives the ranks that band now contains, and each
// band keeps its parent's ceiling-floor spread. tierValue is
// floor + (0.25 + rand*0.75)*range, so E[draw] = 0.375*floor + 0.625*ceiling
// and floor = E - 0.625*spread. Spread is held on purpose: narrowing it drove
// severe inversions to 3.82 and tau-b to 0.747 — too orderly, a regression and
// not a win (same finding as the WR band-width note above).
//
// Mean is exact on all 18 bands. Spread is held on 16: WR T7 now holds three
// ranks that were T8, so its mean falls $17.7 -> $9.3 while its parent spread
// is 0.092 ($18.4) — that solves to a NEGATIVE floor, which is not a price.
// There the floor clamps at 0.01 ($2, the smallest floor in either map) and the
// spread absorbs it; the mean stays exact. Trivially binds on WR T8 too.
//
// This is a SHAPE change with the level held, so it is not expected to move
// spend shares or the round curve. If it does, that is the signal.
export const TIER_MAPS_RECUT = {
  RB: [
    { tier: 1, min: 1,  max: 2,   floor: 0.2644, ceiling: 0.3484, label: "The Anchor" },        // draw $63.4
    { tier: 2, min: 3,  max: 4,   floor: 0.2119, ceiling: 0.3180, label: "Elite Pair" },        // draw $55.6
    { tier: 3, min: 5,  max: 8,   floor: 0.1646, ceiling: 0.2495, label: "High-End Starters" }, // draw $43.5
    { tier: 4, min: 9,  max: 12,  floor: 0.1235, ceiling: 0.2056, label: "Solid Starters" },    // draw $35.0
    { tier: 5, min: 13, max: 17,  floor: 0.0713, ceiling: 0.1583, label: "Value Starters" },    // draw $25.1
    { tier: 6, min: 18, max: 20,  floor: 0.0629, ceiling: 0.1119, label: "Late Starters" },     // draw $18.7
    { tier: 7, min: 21, max: 23,  floor: 0.0490, ceiling: 0.0880, label: "Fliers" },            // draw $14.7
    { tier: 8, min: 24, max: 44,  floor: 0.0189, ceiling: 0.0388, label: "Deep Bench" },        // draw $6.3
    { tier: 9, min: 45, max: 999, floor: 0.0100, ceiling: 0.0200, label: "Lottery Tickets" },   // draw $3.3
  ],
  WR: [
    { tier: 1, min: 1,  max: 2,   floor: 0.2106, ceiling: 0.3065, label: "The Alpha" },         // draw $54.1
    { tier: 2, min: 3,  max: 5,   floor: 0.1808, ceiling: 0.2659, label: "The Alphas" },        // draw $46.8
    { tier: 3, min: 6,  max: 9,   floor: 0.1656, ceiling: 0.2286, label: "Strong Starters" },   // draw $41.0
    { tier: 4, min: 10, max: 14,  floor: 0.0812, ceiling: 0.1832, label: "Solid Starters" },    // draw $29.0
    { tier: 5, min: 15, max: 19,  floor: 0.0419, ceiling: 0.1439, label: "Mid-Tier" },          // draw $21.1
    { tier: 6, min: 20, max: 23,  floor: 0.0404, ceiling: 0.1174, label: "Value Plays" },       // draw $17.7
    { tier: 7, min: 24, max: 27,  floor: 0.0100, ceiling: 0.0684, label: "Fliers" },            // draw $9.3  (spread clamped)
    { tier: 8, min: 28, max: 53,  floor: 0.0100, ceiling: 0.0290, label: "Bench Stashes" },     // draw $4.4  (spread clamped)
    { tier: 9, min: 54, max: 999, floor: 0.0100, ceiling: 0.0200, label: "End of Bench" },      // draw $3.3
  ],
};

// The PRE-RECUT bands, captured by reference before anything can swap them.
// Before chunk 9 these were the default; `_applyTierMap(false)` still restores
// these exact array objects — not copies — so the pre-recut behaviour stays
// bit-reachable from the probe rather than merely re-derivable.
const TIER_MAPS_SHIPPED = { RB: TIER_MAPS.RB, WR: TIER_MAPS.WR };

// THE SHIPPED MAP (chunk 9). The re-cut bands, with the two accepted WR LEVEL
// lifts folded in as absolute floor/ceiling rather than applied at run time:
//   WR T1 x1.15 -> floor 0.2106 -> 0.2512, ceiling 0.3065 -> 0.3471  (draw $54.11 -> $62.23)
//   WR T2 x1.02 -> floor 0.1808 -> 0.1855, ceiling 0.2659 -> 0.2706  (draw $46.80 -> $47.74)
// Both are Object.is-identical to what `_scaleLevels` produces from
// TIER_MAPS_RECUT at those multipliers, so the default path reproduces the
// accepted cell's band state exactly. The RB levels the cell named were 1.00 —
// pass-throughs — so RB ships as TIER_MAPS_RECUT verbatim.
//
// Ladder stays monotone: WR T1 $62.23 > T2 $47.74 > T3 $41.00.
//
// TIER_MAPS_RECUT is kept UNLEVELLED so `_applyTierMap(true, levels)` still
// means what it meant through chunks 4-8 and every stored probe cell still
// reproduces. Do not fold the levels back into it.
const TIER_MAPS_SHIP = {
  RB: TIER_MAPS_RECUT.RB,
  WR: [
    { tier: 1, min: 1,  max: 2,   floor: 0.2512, ceiling: 0.3471, label: "The Alpha" },         // draw $62.2
    { tier: 2, min: 3,  max: 5,   floor: 0.1855, ceiling: 0.2706, label: "The Alphas" },        // draw $47.7
    ...TIER_MAPS_RECUT.WR.slice(2),
  ],
};

// The shipped default IS the ship map, installed at module load. Every reader
// goes through TIER_MAPS[pos] or getTierForRank at CALL time (audited Aug 6
// 2026 — no module captures TIER_MAPS.RB at import time), so assigning the
// properties here is enough and no reader needs to change.
TIER_MAPS.RB = TIER_MAPS_SHIP.RB;
TIER_MAPS.WR = TIER_MAPS_SHIP.WR;

// PROBE ONLY. No shipped src/hooks, src/components or src/engine path writes
// this. Two things write it: the diagnostic harnesses, and utils/devDials.js
// (the dev-only feel-pass activation, stripped from production builds).
// `recut: true` is now the DEFAULT state, reflecting the assignment above.
export const _tierMapSel = { recut: true, levels: null };

// `levels` is an optional per-band LEVEL multiplier, {RB:{1:1.12,3:1.15}, WR:{1:1.25}},
// used by the chunk-4 sweep. It moves a band's MEAN DRAW by the multiplier while
// holding its ceiling-floor spread: E[draw] = 0.375*floor + 0.625*ceiling
// (auction.js:415), so shifting floor and ceiling by the same delta moves the mean
// by that delta and leaves the width alone. Bands with no entry, or an entry of
// exactly 1, are passed through UNTOUCHED — same object, not a copy — so
// _applyTierMap(x) and _applyTierMap(x, {}) are indistinguishable downstream and
// the identity digest still holds.
const _E = t => 0.375 * t.floor + 0.625 * t.ceiling;
function _scaleLevels(arr, byTier) {
  if (!byTier) return arr;
  const moved = arr.some(t => byTier[t.tier] != null && byTier[t.tier] !== 1);
  if (!moved) return arr;
  return arr.map(t => {
    const m = byTier[t.tier];
    if (m == null || m === 1) return t;
    const d = _E(t) * (m - 1);
    return { ...t, floor: Math.round((t.floor + d) * 1e4) / 1e4,
                   ceiling: Math.round((t.ceiling + d) * 1e4) / 1e4 };
  });
}
export function _applyTierMap(recut, levels) {
  const src = recut ? TIER_MAPS_RECUT : TIER_MAPS_SHIPPED;
  TIER_MAPS.RB = _scaleLevels(src.RB, levels?.RB);
  TIER_MAPS.WR = _scaleLevels(src.WR, levels?.WR);
  _tierMapSel.recut = !!recut;
  _tierMapSel.levels = levels || null;
  return _tierMapSel.recut;
}

// Restore the SHIPPED default map. `_applyTierMap(true)` gives the UNLEVELLED
// re-cut, which is a probe state and not what ships — a harness that swept the
// map must call this, not `_applyTierMap(true)`, to get back to defaults.
export function _shipTierMap() {
  TIER_MAPS.RB = TIER_MAPS_SHIP.RB;
  TIER_MAPS.WR = TIER_MAPS_SHIP.WR;
  _tierMapSel.recut = true;
  _tierMapSel.levels = null;
}

// ── THE SFLEX QB TAIL LADDER (aug21) — PROBE ONLY, OFF BY DEFAULT ───
//
// Read ONLY through `_qbTail.sflexTierBranch` in auction.js, and only when
// `sflexCount > 0`. Nothing imports it otherwise, so 1QB is byte-untouched by
// construction and so is every superflex room at the shipped dial state.
//
// WHY IT EXISTS. `TIER_MAPS.QB` above is a 1QB table with no superflex branch:
// tier 5 covers QB12-18 at $3-6 and tier 6 flattens QB19 THROUGH QB999 into one
// $2-3 band. That table drives `tierValue`, the AI's own bid anchor, while the
// DISPLAYED board (QB_SFLEX in simConfig/usePricing) is format-aware — so the
// two QB valuations disagree by an order of magnitude in the tail. Measured
// aug20 (docs/data/qbtail-aug20.md): QB13-24 clears at 0.62x real.
//
// WHAT IT IS NOT. It is NOT a re-banding of `TIER_MAPS.QB`. Tier NUMBERS are
// left exactly where the 1QB map puts them, because they are read far outside
// the anchor: shopping lists key on them ("Punt QB" tiers [4,5,6]), the My
// Guys / Sleepers pools cut on them (QB/TE from T5+), `tierAnchors` is keyed by
// them, `countSubstitutes` compares them, and generatePersonalTierSheet's
// SHIFT_CHANCES and `maxTier` are derived from the array's length. Renumbering
// QB into 9 bands would move all of that at once and the ablation could not
// attribute what it measured. This table replaces the FLOOR/CEILING PAIR only,
// keyed on board rank — which is the whole of what P2 is about.
//
// LEVELS, AND THEY ARE PROVISIONAL. Each band's target mean draw is the CURRENT
// band's mean draw scaled by the measured real/HEAD price ratio for that band:
//
//   band      current E[draw]   HEAD $   real $   ratio   new E[draw]
//   QB13-18   0.024375 (t5)      12.98    20.20    1.56     0.03807
//   QB19-24   0.011875 (t6)       4.92     8.79    1.79     0.02123
//   QB25-32   0.011875 (t6)       3.69     3.89    1.05     0.01243
//   QB33+     0.011875 (t6)       3.45     1.38    0.40     0.00473
//
// HEAD $ is qbtail-aug20's band mean averaged over the two sflex configs;
// QB13-18 and QB19-24 are not separately reported there, so QB19-24 is the mean
// of that report's six named QB19-24 spot anchors and QB13-18 is backed out of
// the QB13-24 band identity at near-equal counts. REAL $ is the qbDemand-24
// corpus column, split within QB13-24 by the per-rank shape in
// docs/data/stephen-sflex-rank-price-aug10.csv rescaled to the corpus band mean
// ($14.52). That corpus is 10 drafts over ~4 league families — a direction and
// a level, never a target to fit to three digits.
//
// floor/ceiling hold the PARENT band's spread (t5 0.015, t6 0.005), same
// convention as the RB/WR re-cut: E[draw] = 0.375*floor + 0.625*ceiling, so
// floor = E - 0.625*spread moves the mean and leaves the width alone.
//
// 🔴 KNOWN INVERSION AT THE QB12/QB13 SEAM. The brief pins QB1-12 (measured
// 1.05x real — on target), so QB12 keeps tier 5's $3-6 while QB13 moves to
// $5.7-8.7. The ANCHOR ladder is therefore non-monotone across that one seam.
// It is deliberate and it is the direct cost of the pin: the real ladder is
// smooth there ($22 at QB12, $21 at QB13) and the engine is uniformly low
// across QB12-18, but only QB13+ is in scope. The displayed board is unmoved
// and still monotone ($17 -> $13), and QB12's sale price is set mostly by that
// board rather than by the anchor. The ablation reports the QB1-12 band mean
// per arm so the architect can see whether it leaked upward.
export const QB_SFLEX_TAIL = [
  { band: 'QB13-18', min: 13, max: 18,  floor: 0.0287, ceiling: 0.0437 },  // draw $7.61 on $200
  { band: 'QB19-24', min: 19, max: 24,  floor: 0.0181, ceiling: 0.0231 },  // draw $4.25
  { band: 'QB25-32', min: 25, max: 32,  floor: 0.0093, ceiling: 0.0143 },  // draw $2.49
  { band: 'QB33+',   min: 33, max: 999, floor: 0.0016, ceiling: 0.0066 },  // draw $0.95
];

// Returns the tier object for a given position and posRank number
// e.g., getTierForRank("RB", 5) → { tier: 1, min: 1, max: 6, floor: 0.225, ceiling: 0.325, label: "The Studs" }
export function getTierForRank(pos, rankNum) {
  const tiers = TIER_MAPS[pos];
  if (!tiers) return null;
  for (const t of tiers) {
    if (rankNum >= t.min && rankNum <= t.max) return t;
  }
  // Beyond last tier — return the final tier
  return tiers[tiers.length - 1];
}

// Returns a tier-based dollar value for a player, using tier midpoint × budget.
// Same logic as ResultsScreen's getTierVal — canonical price source for Team Builder.
export function getTierVal(player, budget = 200) {
  const rn = parseRankNum(getEffectivePosRank(player.name, player.posRank) || player.posRank);
  const tierObj = getTierForRank(player.pos, rn);
  if (!tierObj) return 1;
  return Math.max(1, Math.round(((tierObj.floor + tierObj.ceiling) / 2) * budget));
}

// Generates a per-AI randomized tier sheet where ~15-20% of players shift ±1 tier.
// Uses a seeded PRNG so each AI gets a consistent sheet for the entire draft.
// Returns Map<playerName, adjustedTierNumber>.
export function generatePersonalTierSheet(players, seed) {
  // Seeded PRNG (mulberry32)
  let s = seed | 0;
  const rand = () => {
    s = s + 0x6D2B79F5 | 0;
    let t = Math.imul(s ^ s >>> 15, 1 | s);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };

  // Shift probabilities by position and tier (from spec)
  // Top tiers (T1-2): 5-10%, mid tiers (T3-5): 10-25%, lower tiers (T6+): 10-25%
  const SHIFT_CHANCES = {
    RB:  { 1: 0.05, 2: 0.08, 3: 0.10, 4: 0.15, 5: 0.20, 6: 0.25, 7: 0.25, 8: 0.10, 9: 0.10 },
    // Tiers 10-11 were phantoms — WR has 9 tiers, and maxTier = tiers.length
    // caps the lookup, so those two entries were never read. Removed.
    WR:  { 1: 0.05, 2: 0.10, 3: 0.12, 4: 0.18, 5: 0.25, 6: 0.25, 7: 0.20, 8: 0.10, 9: 0.10 },
    QB:  { 1: 0.05, 2: 0.10, 3: 0.18, 4: 0.22, 5: 0.20, 6: 0.10, 7: 0.10 },
    TE:  { 1: 0.05, 2: 0.08, 3: 0.15, 4: 0.22, 5: 0.20, 6: 0.10 },
    DEF: { 1: 0.05, 2: 0.10, 3: 0.10 },
    K:   { 1: 0.05, 2: 0.10, 3: 0.10 },
  };

  const sheet = new Map();

  for (const player of players) {
    const pos = player.pos;
    const tiers = TIER_MAPS[pos];
    if (!tiers) continue;
    // Parse rank number from ADP posRank (fallback to hardcoded)
    const effectiveRank = getEffectivePosRank(player.name, player.posRank);
    const rankNum = parseRankNum(effectiveRank);
    if (rankNum >= 999) continue;
    const baseTierObj = getTierForRank(pos, rankNum);
    if (!baseTierObj) continue;

    const baseTier = baseTierObj.tier;
    const maxTier = tiers.length;
    const shiftChance = SHIFT_CHANCES[pos]?.[baseTier] || 0.10;

    let adjustedTier = baseTier;
    if (rand() < shiftChance) {
      if (baseTier === 1) {
        adjustedTier = 2;                                    // T1 can only shift down
      } else if (baseTier >= maxTier) {
        adjustedTier = baseTier - 1;                         // Bottom tier can only shift up
      } else {
        adjustedTier = rand() < 0.5 ? baseTier - 1 : baseTier + 1; // 50/50
      }
    }

    sheet.set(player.name, adjustedTier);
  }

  return sheet;
}
